"use client"
import React, { useState, useMemo } from "react";
import { useTranslations } from 'next-intl';
import config from "@/config";
import ButtonCheckout from "@/components/ButtonCheckout";
import CreditPacks from "@/components/anyvideo/CreditPacks";

const SimplePricing = () => {
  const t = useTranslations('Pricing');
  const [pricingType, setPricingType] = useState('yearly'); // 'monthly', 'yearly', 'onetime'
  const [forceUpdate, setForceUpdate] = useState(0);
  const allPlans = config.stripe.plans;
  const originalPlans = allPlans.filter(plan => !plan.isOneTime);
  const popularIndex = config.stripe.popularIndex;

  const isYearly = pricingType === 'yearly';

  const getMobilePlans = () => {
    if (popularIndex >= 0 && popularIndex < originalPlans.length) {
      const popularPlan = originalPlans[popularIndex];
      const otherPlans = originalPlans.filter((_, index) => index !== popularIndex);
      return [popularPlan, ...otherPlans];
    }
    return originalPlans;
  };

  const getOriginalIndex = (plan, isMobile = false) => {
    return originalPlans.findIndex(p => p.name === plan.name);
  };

  const formatCredits = (credits) => {
    if (credits >= 1000) {
      const k = credits / 1000;
      // 如果是整数，不显示小数点
      if (k % 1 === 0) {
        return k.toFixed(0) + 'K';
      }
      // 如果有小数，显示一位小数
      return k.toFixed(1) + 'K';
    }
    return credits.toString();
  };

  const getCreditValue = useMemo(() => {
    return (plan, yearly = isYearly) => {
      const credits = yearly ? plan.yearlyCredits : plan.credits;
      const price = yearly ? (plan.yearlyPrice * 12) : plan.price;
      if (credits === 0 || price === 0) return '0.00';
      return ((price / credits) * 100).toFixed(2);
    };
  }, [isYearly]);

  const getPlanInfo = useMemo(() => {
    return (planName) => {
      const plan = allPlans.find(p => p.name === planName);
      if (!plan) {
        return { credits: '0', period: 'credits', features: [], creditValue: '0.00' };
      }

      const credits = isYearly ? plan.yearlyCredits : plan.credits;
      const formattedCredits = formatCredits(credits);
      const creditValue = getCreditValue(plan);

      let features = [];
      
      if (planName === 'Basic') {
        features = ['Standard Processing', 'No Watermark', 'Email Support'];
        if (isYearly) {
          features.push('Commercial License', 'Priority Support', 'Batch Video Generation (Coming Soon)', '1080p Video Quality (Coming Soon)');
        }
      } else if (planName === 'Plus') {
        features = ['Priority Processing', 'No Watermark', 'Chat Support'];
        if (isYearly) {
          features.push('Commercial License', 'Priority Support', 'Batch Video Generation (Coming Soon)', '1080p Video Quality (Coming Soon)');
        }
      } else if (planName === 'Pro') {
        features = ['Ultra-Fast Processing', 'No Watermark', 'Dedicated Support'];
        if (isYearly) {
          features.push('Commercial License', 'Priority Support', 'Batch Video Generation (Coming Soon)', '1080p Video Quality (Coming Soon)');
        }
      }

      return {
        credits: formattedCredits,
        period: isYearly ? 'credits/year' : 'credits/month',
        features: features,
        creditValue: creditValue
      };
    };
  }, [isYearly, allPlans]);

  const mobilePlans = getMobilePlans();

  return (
    <div className="py-2 md:py-6">
      <div className="max-w-5xl mx-auto px-2 md:px-4 h-full">
        <div className="text-center mb-3 md:mb-8">
          <h2 className="text-lg md:text-2xl lg:text-3xl font-bold text-white mb-2 md:mb-4">
            <span className="bg-gradient-to-r from-green-400 via-cyan-400 to-green-500 text-transparent bg-clip-text drop-shadow-sm">
              {t('main.title')}
            </span>
          </h2>
          
          <div className="flex items-center justify-center space-x-4">
            <div className="flex items-center">
              <span className="text-xs md:text-sm font-medium text-green-400">{t('main.creditsUsage.veo3Fast')}</span>
              <span className="text-xs md:text-sm text-gray-300 ml-1">{t('main.creditsUsage.fastCredits')}</span>
            </div>
            <div className="text-gray-500">•</div>
            <div className="flex items-center">
              <span className="text-xs md:text-sm font-medium text-green-400">{t('main.creditsUsage.veo3Standard')}</span>
              <span className="text-xs md:text-sm text-gray-300 ml-1">{t('main.creditsUsage.standardCredits')}</span>
            </div>
          </div>
        </div>

        {/* 价格类型切换器 - 三个选项 */}
        <div className="flex justify-center mb-4 md:mb-8">
          <div className="inline-flex bg-gray-800 rounded-lg border border-gray-700 text-xs md:text-sm">
            <button
              onClick={() => setPricingType('monthly')}
              className={`px-3 py-1.5 md:px-4 md:py-2 font-medium rounded-lg transition-all ${
                pricingType === 'monthly'
                  ? 'bg-green-500 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {t('tabs.monthly')}
            </button>
            <button
              onClick={() => setPricingType('yearly')}
              className={`px-3 py-1.5 md:px-4 md:py-2 font-medium rounded-lg transition-all ${
                pricingType === 'yearly'
                  ? 'bg-green-500 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {t('tabs.yearly')}
              <span className="ml-1 text-xs text-green-400 font-medium">
                -40%
              </span>
            </button>
            <button
              onClick={() => setPricingType('onetime')}
              className={`px-3 py-1.5 md:px-4 md:py-2 font-medium rounded-lg transition-all ${
                pricingType === 'onetime'
                  ? 'bg-green-500 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {t('tabs.onetime')}
            </button>
          </div>
        </div>

        {/* 订阅计划内容 */}
        {pricingType !== 'onetime' && (
          <>
            {/* 移动端 - 竖向排列，紧凑布局 */}
            <div className="md:hidden">
              <div className="space-y-3">
                {mobilePlans.map((plan, mobileIndex) => {
                  const planInfo = getPlanInfo(plan.name);
                  const originalIndex = getOriginalIndex(plan, true);
                  const isPopular = originalIndex === popularIndex;
                  
                  return (
                    <div key={`${plan.name}-${pricingType}`} className="relative">
                      {isPopular && (
                        <div className="absolute -top-1.5 left-1/2 transform -translate-x-1/2 z-10">
                          <div className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {t('general.popularLabel')}
                          </div>
                        </div>
                      )}
                      
                      <div className={`relative p-4 rounded-lg ${
                        isPopular 
                          ? 'bg-gray-800 border-2 border-green-500' 
                          : 'bg-gray-800 border border-gray-700'
                      }`}>
                        {/* 计划名称和价格 */}
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="text-base font-bold text-white">
                              {plan.name === 'Pro' ? t('general.enterprise') : plan.name}
                            </h3>
                            <div className="text-2xl font-bold text-white" key={`price-${plan.name}-${pricingType}`}>
                              ${isYearly ? plan.yearlyPrice : plan.price}
                              <span className="text-sm text-gray-400">{t('general.pricePerMonth')}</span>
                            </div>
                            {isYearly && plan.price !== 0 && (
                              <div className="text-xs text-gray-400">
                                ${(plan.yearlyPrice * 12).toFixed(0)}{t('general.pricePerYear')}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            {plan.price === 0 ? (
                              <button disabled className="px-3 py-1.5 rounded-lg bg-gray-700 text-gray-400 text-xs font-medium">
                                {t('general.currentPlan')}
                              </button>
                            ) : (
                              <ButtonCheckout
                                plan={plan}
                                isYearly={isYearly}
                                key={`btn-${plan.name}-${pricingType}`}
                                className={`px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-xl active:scale-95 ${
                                  isPopular
                                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-green-500/40 hover:shadow-green-500/60'
                                    : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-500 hover:to-gray-600 shadow-gray-700/40'
                                }`}
                              >
                                {t('general.getCredits', { credits: planInfo.credits })}
                              </ButtonCheckout>
                            )}
                          </div>
                        </div>

                        {/* 功能特性和性价比 - 左右布局 */}
                        <div className="flex items-center justify-between py-3 border-t border-gray-700" key={`features-${plan.name}-${pricingType}`}>
                          <div className="text-left">
                            <div className="text-xs text-gray-300 space-y-1" key={`credits-mobile-${plan.name}-${pricingType}`}>
                              <div className="flex items-center">
                                <span className="text-green-400 mr-1">✓</span>
                                <span>{t('general.noWatermark')}</span>
                              </div>
                              <div className="flex items-center">
                                <span className={`mr-1 ${isYearly && plan.price !== 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {isYearly && plan.price !== 0 ? '✓' : '✗'}
                                </span>
                                <span className={isYearly && plan.price !== 0 ? 'text-green-400' : 'text-gray-400'}>{t('general.commercialLicense')}</span>
                              </div>
                              {!isYearly && (
                                <div className="flex items-center">
                                  <span className="text-red-400 mr-1">✗</span>
                                  <span className="text-gray-400">{t('general.batchProcessing')}</span>
                                </div>
                              )}
                              {isYearly && (
                                <div className="flex items-center">
                                  <span className="text-green-400 mr-1">✓</span>
                                  <span className="text-green-400">{t('general.batchProcessingComingSoon')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right" key={`credit-value-mobile-${plan.name}-${pricingType}`}>
                            <div className="text-base font-medium text-gray-300">
                              ${planInfo.creditValue}
                            </div>
                            <div className="text-xs text-gray-500">
                              {t('general.per100Credits')}
                            </div>
                          </div>
                        </div>

                        {/* 简化版年费升级督促 */}
                        {!isYearly && plan.price !== 0 && (
                          <button 
                            onClick={() => setPricingType('yearly')}
                            className="mt-2 w-full px-2 py-1 bg-gradient-to-r from-orange-500/5 to-green-500/5 border-l-2 border-orange-400 rounded-r text-left hover:from-orange-500/10 hover:to-green-500/10 transition-all"
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-orange-400 text-xs font-medium">
                                {t('general.saveWithYearly', { amount: (plan.price * 12 - plan.yearlyPrice * 12).toFixed(0) })}
                              </div>
                              <div className="text-orange-300/80 text-xs">
                                →
                              </div>
                            </div>
                          </button>
                        )}

                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* 移动端信任信号 - 紧凑版 */}
              <div className="mt-4 text-center">
                <a 
                  href="mailto:support@wan22.io" 
                  className="text-xs text-green-400 hover:text-green-300"
                >
                  {t('general.contactSupport')}
                </a>
              </div>
            </div>

            {/* 桌面端价格卡片 */}
            <div className="hidden md:grid md:grid-cols-3 gap-6">
              {originalPlans.map((plan, index) => {
                const planInfo = getPlanInfo(plan.name);
                const isPopular = index === popularIndex;
                
                return (
                  <div key={`${plan.name}-${pricingType}`} className="relative">
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                        <div className="bg-green-500 text-white text-sm font-bold px-4 py-1 rounded-full shadow-lg">
                          {t('mostPopular')}
                        </div>
                      </div>
                    )}
                    
                    <div className={`relative p-6 rounded-xl transition-all hover:scale-105 ${
                      isPopular 
                        ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-green-500 shadow-2xl shadow-green-500/20' 
                        : 'bg-gray-800 border border-gray-700 hover:border-gray-600'
                    }`}>
                      {/* 计划名称和价格信息 - 参考移动端布局 */}
                      <div className="mb-4" key={`price-info-${plan.name}-${pricingType}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="text-xl font-bold text-white mb-1">
                              {plan.name === 'Pro' ? t('general.enterprise') : plan.name}
                            </h3>
                            <div className="text-3xl font-bold text-white">
                              ${isYearly ? plan.yearlyPrice : plan.price}
                              <span className="text-lg text-gray-400">{t('general.pricePerMonth')}</span>
                            </div>
                            {isYearly && plan.price !== 0 && (
                              <div className="text-sm text-gray-400 mt-1">
                                ${(plan.yearlyPrice * 12).toFixed(0)}{t('general.pricePerYear')}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-lg text-green-400 font-medium">
                              {planInfo.credits} {t('creditsLabel')}
                            </div>
                            <div className="text-sm text-gray-400">
                              /{isYearly ? t('general.pricePerYear').replace('/', '') : t('general.pricePerMonth').replace('/', '')}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 功能特性和价格信息 - 左右布局 */}
                      <div className="mb-6" key={`features-desktop-${plan.name}-${pricingType}`}>
                        <div className="flex items-center justify-between py-3 border-t border-gray-700">
                          <div className="text-left">
                            <div className="text-sm text-gray-300 space-y-1">
                              <div className="flex items-center">
                                <span className="text-green-400 mr-2">✓</span>
                                <span>{t('general.noWatermark')}</span>
                              </div>
                              <div className="flex items-center">
                                <span className={`mr-1 ${isYearly && plan.price !== 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {isYearly && plan.price !== 0 ? '✓' : '✗'}
                                </span>
                                <span className={isYearly && plan.price !== 0 ? 'text-green-400' : 'text-gray-400'}>{t('general.commercialLicense')}</span>
                              </div>
                              {!isYearly && (
                                <div className="flex items-center">
                                  <span className="text-red-400 mr-2">✗</span>
                                  <span className="text-gray-400">{t('general.batchProcessing')}</span>
                                </div>
                              )}
                              {isYearly && (
                                <div className="flex items-center">
                                  <span className="text-green-400 mr-2">✓</span>
                                  <span className="text-green-400">{t('general.batchProcessingHD')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-medium text-gray-300">
                              ${planInfo.creditValue}
                            </div>
                            <div className="text-xs text-gray-500">
                              {t('general.per100Credits')}
                            </div>
                          </div>
                        </div>
                        
                        {/* 简化版年费升级督促 - 桌面端 */}
                        {!isYearly && plan.price !== 0 && (
                          <button 
                            onClick={() => setPricingType('yearly')}
                            className="mt-3 w-full px-3 py-2 bg-gradient-to-r from-orange-500/8 to-green-500/8 border border-orange-400/30 rounded-lg hover:from-orange-500/12 hover:to-green-500/12 transition-all"
                          >
                            <div className="flex items-center justify-between">
                              <div className="text-orange-400 text-sm font-semibold">
                                {t('general.saveWithYearly', { amount: (plan.price * 12 - plan.yearlyPrice * 12).toFixed(0) })}
                              </div>
                              <div className="text-orange-300 text-xs">
                                {t('general.upgradeNow')} →
                              </div>
                            </div>
                          </button>
                        )}
                      </div>

                      {/* 购买按钮 - 参考移动端样式 */}
                      <div className="mt-4">
                        {plan.price === 0 ? (
                          <button disabled className="w-full py-3 rounded-xl bg-gray-700 text-gray-400 font-medium">
                            {t('general.currentPlan')}
                          </button>
                        ) : (
                          <ButtonCheckout
                            plan={plan}
                            isYearly={isYearly}
                            key={`btn-desktop-${plan.name}-${pricingType}`}
                            className={`w-full py-3 rounded-xl font-bold text-sm transition-all shadow-xl active:scale-95 ${
                              isPopular
                                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-green-500/40 hover:shadow-green-500/60'
                                : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white hover:from-gray-500 hover:to-gray-600 shadow-gray-700/40'
                            }`}
                          >
                            {t('general.getCredits', { credits: planInfo.credits })}
                          </ButtonCheckout>
                        )}
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>

            {/* 桌面端信任信号 */}
            <div className="hidden md:block mt-6 md:mt-8 text-center">
              
              <div className="inline-flex items-center px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-gray-800/50 border border-gray-700/50 hover:border-green-500/50 transition-all duration-300">
                <svg className="w-3 h-3 md:w-4 md:h-4 text-green-400 mr-1.5 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a 
                  href="mailto:support@wan22.io" 
                  className="text-xs md:text-sm text-green-400 hover:text-green-300 transition-colors font-medium"
                >
                  {t('general.contactSupport')}
                </a>
              </div>
            </div>
          </>
        )}

        {/* 一次性积分包内容 */}
        {pricingType === 'onetime' && (
          <div>
            <CreditPacks />
          </div>
        )}
      </div>
    </div>
  );
};

export default SimplePricing; 